interface BackgroundShapesProps {
  variant?: 'hero' | 'features' | 'testimonials' | 'pricing' | 'about' | 'auth';
}

export const BackgroundShapes = ({ variant = 'hero' }: BackgroundShapesProps) => {
  const getShapes = () => {
    switch (variant) {
      case 'hero':
        return (
          <>
            {/* Large organic blob - top right */}
            <svg 
              className="absolute top-0 right-0 w-[600px] h-[800px] opacity-20 text-primary"
              viewBox="0 0 702 992"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                fill="currentColor"
                fillRule="evenodd" 
                d="M630.57 990.697c-101.386-8.027-183.447-87.17-237.491-173.36-65.181-103.952-105.161-224.42-190.569-315.045-56.209-59.64-132.9-104.492-172.8-178.173-45.507-84.04-35.723-186.941 15.023-266.22 73.926-115.488 213.81-178.54 345.958-195.444 162.68-20.814 337.154 15.61 490.652 69.325 112.315 39.302 229.521 122.837 214.74 256.414-13.972 126.248-127.72 214.697-187.424 319.362-29.958 52.513-45.28 113.287-43.794 173.733 1.101 44.934 11.232 90.258 2.863 134.42-18.615 98.242-118.112 172.833-216.357 175.514-6.95.191-13.892.02-20.8-.526z"
              />
            </svg>
            {/* Gradient orb */}
            <div className="absolute top-40 left-20 w-96 h-96 bg-gradient-to-br from-secondary/30 to-accent/20 rounded-full blur-3xl" />
          </>
        );
      
      case 'features':
        return (
          <>
            {/* Circular blob - left side */}
            <svg 
              className="absolute top-1/4 left-0 w-[400px] h-[600px] opacity-15 text-secondary"
              viewBox="0 0 400 600"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="100" cy="300" r="250" fill="currentColor" />
            </svg>
            {/* Small dots pattern */}
            <div className="absolute top-20 right-40 w-32 h-32 opacity-20">
              <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" className="text-primary">
                <circle cx="10" cy="10" r="2" fill="currentColor" />
                <circle cx="30" cy="10" r="2" fill="currentColor" />
                <circle cx="50" cy="10" r="2" fill="currentColor" />
                <circle cx="70" cy="10" r="2" fill="currentColor" />
                <circle cx="90" cy="10" r="2" fill="currentColor" />
                <circle cx="10" cy="30" r="2" fill="currentColor" />
                <circle cx="30" cy="30" r="2" fill="currentColor" />
                <circle cx="50" cy="30" r="2" fill="currentColor" />
                <circle cx="70" cy="30" r="2" fill="currentColor" />
                <circle cx="90" cy="30" r="2" fill="currentColor" />
              </svg>
            </div>
          </>
        );
      
      case 'testimonials':
        return (
          <>
            {/* Wave shape - bottom */}
            <svg 
              className="absolute bottom-0 left-0 w-full h-[300px] opacity-10 text-accent"
              viewBox="0 0 1440 320"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                fill="currentColor"
                d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,138.7C960,139,1056,117,1152,122.7C1248,128,1344,160,1392,176L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
              />
            </svg>
            {/* Gradient orb */}
            <div className="absolute top-1/3 right-20 w-80 h-80 bg-gradient-to-tr from-primary/20 to-secondary/30 rounded-full blur-3xl" />
          </>
        );
      
      case 'pricing':
        return (
          <>
            {/* Geometric squares */}
            <div className="absolute top-40 right-20 w-64 h-64 opacity-10">
              <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="text-primary">
                <rect x="10" y="10" width="80" height="80" fill="currentColor" transform="rotate(15 50 50)" />
                <rect x="110" y="110" width="60" height="60" fill="currentColor" transform="rotate(-20 140 140)" />
              </svg>
            </div>
            {/* Curved line */}
            <svg 
              className="absolute bottom-20 left-0 w-[500px] h-[400px] opacity-15 text-secondary"
              viewBox="0 0 500 400"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                d="M0,200 Q125,100 250,200 T500,200"
                fill="none"
                stroke="currentColor"
                strokeWidth="60"
              />
            </svg>
            {/* Circle ring */}
            <svg 
              className="absolute top-1/2 left-40 w-[300px] h-[300px] opacity-10 text-accent"
              viewBox="0 0 300 300"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="150" cy="150" r="120" fill="none" stroke="currentColor" strokeWidth="30" />
              <circle cx="150" cy="150" r="60" fill="currentColor" opacity="0.5" />
            </svg>
            {/* Gradient orb */}
            <div className="absolute bottom-1/4 right-40 w-96 h-96 bg-gradient-to-br from-primary/20 to-secondary/30 rounded-full blur-3xl" />
          </>
        );
      
      case 'about':
        return (
          <>
            {/* Multiple floating circles */}
            <svg 
              className="absolute top-20 right-40 w-[300px] h-[300px] opacity-15 text-primary"
              viewBox="0 0 300 300"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="100" cy="100" r="80" fill="currentColor" />
              <circle cx="200" cy="180" r="60" fill="currentColor" opacity="0.6" />
              <circle cx="50" cy="220" r="40" fill="currentColor" opacity="0.4" />
            </svg>
            {/* Organic blob - bottom left */}
            <svg 
              className="absolute bottom-40 left-0 w-[500px] h-[700px] opacity-20 text-secondary"
              viewBox="0 0 702 992"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                fill="currentColor"
                fillRule="evenodd" 
                d="M71.43 1.303c101.386 8.027 183.447 87.17 237.491 173.36 65.181 103.952 105.161 224.42 190.569 315.045 56.209 59.64 132.9 104.492 172.8 178.173 45.507 84.04 35.723 186.941-15.023 266.22-73.926 115.488-213.81 178.54-345.958 195.444-162.68 20.814-337.154-15.61-490.652-69.325C-291.655 1020.918-408.861 937.383-394.08 803.806c13.972-126.248 127.72-214.697 187.424-319.362 29.958-52.513 45.28-113.287 43.794-173.733-1.101-44.934-11.232-90.258-2.863-134.42 18.615-98.242 118.112-172.833 216.357-175.514 6.95-.191 13.892-.02 20.8.526z"
              />
            </svg>
            {/* Wavy lines */}
            <svg 
              className="absolute top-1/3 left-20 w-[400px] h-[200px] opacity-10 text-accent"
              viewBox="0 0 400 200"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                d="M0,100 Q50,50 100,100 T200,100 T300,100 T400,100"
                fill="none"
                stroke="currentColor"
                strokeWidth="20"
              />
              <path 
                d="M0,140 Q50,90 100,140 T200,140 T300,140 T400,140"
                fill="none"
                stroke="currentColor"
                strokeWidth="20"
                opacity="0.6"
              />
            </svg>
            {/* Gradient orb */}
            <div className="absolute top-1/2 right-20 w-80 h-80 bg-gradient-to-tr from-secondary/30 to-primary/20 rounded-full blur-3xl" />
          </>
        );
      
      case 'auth':
        return (
          <>
            {/* Large circle - top right */}
            <svg 
              className="absolute -top-20 -right-20 w-[500px] h-[500px] opacity-15 text-primary"
              viewBox="0 0 500 500"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="250" cy="250" r="200" fill="currentColor" />
            </svg>
            {/* Small circles pattern */}
            <div className="absolute bottom-40 left-40 w-48 h-48 opacity-20">
              <svg viewBox="0 0 150 150" xmlns="http://www.w3.org/2000/svg" className="text-secondary">
                <circle cx="25" cy="25" r="15" fill="currentColor" />
                <circle cx="75" cy="25" r="15" fill="currentColor" opacity="0.7" />
                <circle cx="125" cy="25" r="15" fill="currentColor" opacity="0.5" />
                <circle cx="25" cy="75" r="15" fill="currentColor" opacity="0.7" />
                <circle cx="75" cy="75" r="15" fill="currentColor" opacity="0.5" />
                <circle cx="125" cy="75" r="15" fill="currentColor" opacity="0.3" />
                <circle cx="25" cy="125" r="15" fill="currentColor" opacity="0.5" />
                <circle cx="75" cy="125" r="15" fill="currentColor" opacity="0.3" />
              </svg>
            </div>
            {/* Curved blob */}
            <svg 
              className="absolute bottom-0 right-0 w-[600px] h-[700px] opacity-10 text-accent"
              viewBox="0 0 702 992"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                fill="currentColor"
                fillRule="evenodd" 
                d="M350 100c-80 50-150 120-180 220-30 100-20 220 40 300 60 80 160 120 260 110 100-10 180-70 220-160 40-90 30-200-20-280-50-80-140-130-240-140-40-5-80-10-80-50z"
              />
            </svg>
            {/* Gradient orbs */}
            <div className="absolute top-40 left-1/4 w-96 h-96 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-full blur-3xl" />
            <div className="absolute bottom-1/3 right-1/3 w-72 h-72 bg-gradient-to-tr from-accent/20 to-primary/20 rounded-full blur-3xl" />
          </>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }}>
      {getShapes()}
    </div>
  );
};
